import { snakeCase } from "../../utils";
import { DELIMITER } from "../constants";
import type { EventDefinition, ParamsType } from "../types";
import { redact } from "./questionnaire/redaction";

/**
 * Event definitions for Snowplow
 * @type {EventDefinition[]}
 * @property {string} name - The name of the event, to use when triggering
 * @property {string} type - The type of the event (structured | unstructured)
 * @property {makePayload} makePayload
 *   - Function that creates the payload for the event;
 *   - Allows optional params object to be passed in
 *
 * @example
 * Parent
 * import { getSnowplowConfig, SnowplowProvider } from "@simplybusiness/services";
 * const snowplowProps = getSnowplowConfig(pageData);
 * <SnowplowProvider scripts={snowplowProps!}>{children}</SnowplowProvider>
 *
 * Child
 * import { useSnowplowContext } from "@simplybusiness/services";
 * const { snowplow } = useSnowplowContext();
 * const handlerFunction = () => snowplow?.trigger("eventNameHere");
 */

export const personalisedCoverEventDefinitions: EventDefinition[] = [
  {
    name: "seeMoreClicked",
    type: "structured",
    makePayload: _ => ({
      category: "personalised_cover",
      action: "see_more_clicked",
      label: "page",
      property: "personalised_cover",
    }),
  },
  {
    name: "selectPackageClicked",
    type: "structured",
    makePayload: _ => ({
      category: "personalised_cover",
      action: "select_package_clicked",
      label: "page",
      property: "personalised_cover",
    }),
  },
  {
    name: "validationShown",
    type: "structured",
    makePayload: params => {
      const { cover, question } = params as ParamsType;

      return {
        category: "personalised_cover",
        action: "validation_shown",
        label: "cover_question",
        property: `${snakeCase(cover)}_${snakeCase(question)}`,
      };
    },
  },
  {
    name: "coverQuestionAnswered",
    type: "unstructured",
    makePayload: params => {
      const { context } = params as ParamsType;
      const { question, answer } = redact(params as ParamsType);
      // Flatten the answer to a delimited string
      const flatAnswer = Array.isArray(answer)
        ? answer.join(DELIMITER)
        : answer;

      return {
        schema:
          "iglu:com.simplybusiness/form_question_answered/jsonschema/1-0-1",
        data: {
          site: context.site,
          page_name: "personalised_cover",
          section_name: "Personalised Cover Questions",
          question,
          answer: flatAnswer,
        },
      };
    },
  },
  {
    name: "limitLinkClicked",
    type: "structured",
    makePayload: params => {
      const { label } = params as ParamsType;

      return {
        category: "personalised_cover",
        action: "limit_link_clicked",
        label,
        property: "link_clicked",
      };
    },
  },
  {
    name: "limitChanged",
    type: "structured",
    makePayload: params => {
      const { label, limit } = params as ParamsType;

      return {
        category: "personalised_cover",
        action: "limit_changed",
        label,
        property: limit,
      };
    },
  },
  {
    name: "coverPresented",
    type: "structured",
    makePayload: params => {
      const { property } = params as ParamsType;

      return {
        category: "personalised_cover",
        action: "cover_presented",
        label: "cover_name",
        property,
      };
    },
  },
  {
    name: "coverDetailsClicked",
    type: "structured",
    makePayload: params => {
      const { property } = params as ParamsType;

      return {
        category: "personalised_cover",
        action: "cover_details_clicked",
        label: "cover_name",
        property,
      };
    },
  },
  {
    name: "additionalQuestionsClicked",
    type: "structured",
    makePayload: params => {
      const { property } = params as ParamsType;

      return {
        category: "personalised_cover",
        action: "cover_details_clicked",
        label: "cover_name",
        property,
      };
    },
  },
  {
    name: "chatbotPromptClicked",
    type: "structured",
    makePayload: params => {
      const { label, property } = params as ParamsType;

      return {
        category: "personalised_cover",
        action: "chatbot_prompt_clicked",
        label: property,
        property: label,
      };
    },
  },
];
